import { AdvancedMarker } from "@vis.gl/react-google-maps";
import { Waypoint } from "@/api.types";
import { ZOOM_LEVELS } from "@/components/markers/marker.util";
import { logger } from "@/services/logger";
import { useTripsStore } from "@/screens/dashboard/trip-temp-order.store";
import { useShortcutsStore } from "@/screens/dashboard/shortcuts.store";
import { useState, useMemo, useCallback } from "react";

interface WayPointsMarkersProps {
  wayPoints: Waypoint[][];
  zoomLevel: number;
}

export function WaypointsMarkers({
  wayPoints,
  zoomLevel,
}: WayPointsMarkersProps) {
  const temporaryWaypointOrder = useTripsStore(
    (state) => state.temporaryWaypointOrder
  );

  // Get trip filter from shortcuts store
  const tripFilter = useShortcutsStore((state) => state.tripFilter);

  // State for hovered waypoint
  const [hoveredWaypointId, setHoveredWaypointId] = useState<string | null>(
    null
  );

  // Calculate marker size scale based on zoom level
  const getScale = useCallback(() => {
    if (zoomLevel < ZOOM_LEVELS.MEDIUM) return 0.5;
    if (zoomLevel < ZOOM_LEVELS.LARGE) return 0.75;
    return 1;
  }, [zoomLevel]);

  // Calculate progress indicator height based on zoom level
  const getProgressIndicatorHeight = useCallback(() => {
    if (zoomLevel < ZOOM_LEVELS.MEDIUM) return 10; // Smaller height when zoomed out
    if (zoomLevel < ZOOM_LEVELS.LARGE) return 14;
    return 18; // Full height when zoomed in
  }, [zoomLevel]);

  const showCounts = zoomLevel >= ZOOM_LEVELS.MEDIUM;

  // Calculate total marker height including progress indicator
  const getMarkerTotalHeight = useCallback(() => {
    // Main marker box (10px) + progress indicator height + small gap
    return 10 + getProgressIndicatorHeight() + 2;
  }, [getProgressIndicatorHeight]);

  // Apply trip filter to waypoints
  const filteredWaypoints = useMemo(() => {
    return wayPoints.map((tripWaypoints) =>
      tripWaypoints.filter((waypoint) => {
        const tripId = waypoint.tripId;
        if (!tripId) return true; // Keep waypoints without a trip

        // If showing all trips or this trip is specifically visible
        if (tripFilter.showAll || tripFilter.visibleTripIds.includes(tripId)) {
          return true;
        }

        // If this trip is specifically hidden
        if (tripFilter.hiddenTripIds.includes(tripId)) {
          return false;
        }

        return tripFilter.showAll; // Default to the showAll setting
      })
    );
  }, [wayPoints, tripFilter]);

  // Pre-process waypoints to set z-indexes and track overlapping waypoints
  const waypointData = useMemo(() => {
    const waypointZIndexes = new Map<string, number>();
    const waypointPositionData = new Map<
      string,
      {
        connectorLength: number;
        verticalOffset: number;
        tripColors: string[];
      }
    >();

    // Count overlapping waypoints
    const locationMap = new Map<string, number>();
    const locationWaypointsMap = new Map<string, string[]>();
    const locationColorsMap = new Map<string, string[]>();

    filteredWaypoints.flat().forEach((waypoint: Waypoint) => {
      if (!waypoint.locationKey) return;

      if (locationMap.has(waypoint.locationKey)) {
        const currentCount = locationMap.get(waypoint.locationKey) ?? 1;
        locationMap.set(waypoint.locationKey, currentCount + 1);

        const waypointsAtThisLocation =
          locationWaypointsMap.get(waypoint.locationKey) ?? [];
        waypointsAtThisLocation.push(waypoint.id);
        locationWaypointsMap.set(waypoint.locationKey, waypointsAtThisLocation);

        // Store trip colors for each location
        const colorsAtThisLocation =
          locationColorsMap.get(waypoint.locationKey) ?? [];
        const tripColor = waypoint.trip?.color ?? "bg-blue-800";
        if (!colorsAtThisLocation.includes(tripColor)) {
          colorsAtThisLocation.push(tripColor);
        }
        locationColorsMap.set(waypoint.locationKey, colorsAtThisLocation);
      } else {
        locationMap.set(waypoint.locationKey, 1);
        locationWaypointsMap.set(waypoint.locationKey, [waypoint.id]);
        locationColorsMap.set(waypoint.locationKey, [
          waypoint.trip?.color ?? "bg-blue-800",
        ]);
      }
    });

    // Approximate height of a marker
    const markerHeight = getMarkerTotalHeight();

    // Vertical offset between stacked markers (as percentage of marker height)
    const stackingOffset = markerHeight * 1.1;

    // Set z-indexes and positioning based on sequence
    locationWaypointsMap.forEach((waypointIds, locationKey) => {
      const totalAtLocation = locationMap.get(locationKey) ?? 0;
      const hasOverlap = totalAtLocation > 1;
      const tripColors = locationColorsMap.get(locationKey) ?? [];

      waypointIds.forEach((waypointId, index) => {
        // Higher sequence numbers (added later) get higher z-index so they appear on top
        const baseZIndex = totalAtLocation - index;
        waypointZIndexes.set(waypointId, baseZIndex);

        if (hasOverlap) {
          // Calculate vertical offset for staggered effect
          // Bottom marker (index = totalAtLocation - 1) has the largest offset
          // Top marker (index = 0) has no offset
          const verticalOffset = (totalAtLocation - index - 1) * stackingOffset;

          // Calculate connector length based on vertical offset
          // Base connector is 3px, plus vertical offset to reach the actual map point
          const connectorLength = 3 + verticalOffset;

          waypointPositionData.set(waypointId, {
            connectorLength,
            verticalOffset,
            tripColors,
          });
        } else {
          // Non-overlapping waypoints have default values
          waypointPositionData.set(waypointId, {
            connectorLength: 3,
            verticalOffset: 0,
            tripColors,
          });
        }
      });
    });

    return { waypointZIndexes, waypointPositionData };
  }, [filteredWaypoints, getMarkerTotalHeight]);

  // Generate conic gradient for overlapping waypoints
  const generateConicGradient = useCallback((colors: string[]) => {
    // If only one color, just use solid color
    if (colors.length <= 1) {
      return colors[0] || "bg-blue-800";
    }

    // Limit to showing max 4 colors in the merged marker
    const displayedColors = colors.slice(0, 4);

    // Calculate segment percentage and positions
    const segmentPercent = 100 / displayedColors.length;

    let gradientString = "conic-gradient(";
    displayedColors.forEach((color, index) => {
      const startPercent = index * segmentPercent;
      const endPercent = (index + 1) * segmentPercent;

      gradientString += `${color} ${startPercent}% ${endPercent}%`;

      // Add comma if not the last segment
      if (index < displayedColors.length - 1) {
        gradientString += ", ";
      }
    });
    gradientString += ")";

    return gradientString;
  }, []);

  // --- Event Handlers ---
  const handleMouseEnter = useCallback((waypointId: string) => {
    setHoveredWaypointId(waypointId);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHoveredWaypointId(null);
  }, []);

  // --- Rendering Logic ---
  return filteredWaypoints.flat().map((waypoint: Waypoint) => {
    const { locationKey, sequence, tasks, id } = waypoint;

    if (!locationKey) {
      logger.warn(`Waypoint ${id} has no locationKey`);
      return null;
    }

    const coordinates = locationKey.split(",");
    const lat = parseFloat(coordinates[0]);
    const lng = parseFloat(coordinates[1]);

    if (isNaN(lat) || isNaN(lng)) {
      logger.warn(`Invalid coordinates for waypoint ${id}: ${locationKey}`);
      return null;
    }

    const pickupCount = tasks.filter((task) => task.type === "PICKUP").length;
    const deliveryCount = tasks.filter(
      (task) => task.type === "DELIVERY"
    ).length;
    const completedTasksCount = tasks.filter((task) =>
      ["COMPLETED", "FAILED", "CANCELLED"].includes(task.status)
    ).length;
    const completionPercentage = (completedTasksCount / tasks.length) * 100;
    const tripColor = waypoint.trip?.color ?? "bg-blue-800";
    const hasTemporarySequence = temporaryWaypointOrder?.[waypoint.id];

    // Get z-index
    const baseZIndex = waypointData.waypointZIndexes.get(id) ?? 1;
    const isIndividuallyHovered = hoveredWaypointId === id;
    const zIndex = isIndividuallyHovered ? 100 : baseZIndex;

    // Get positioning data
    const positionData = waypointData.waypointPositionData.get(id) ?? {
      connectorLength: 3,
      verticalOffset: 0,
      tripColors: [tripColor],
    };

    // Get marker size scale
    const markerScale = getScale();

    // Determine connector dot style - pie chart gradient for overlapping points
    const dotBackground =
      positionData.tripColors.length > 1
        ? generateConicGradient(positionData.tripColors)
        : tripColor;

    const hasMultipleColors = positionData.tripColors.length > 1;

    return (
      <AdvancedMarker key={id} position={{ lat, lng }}>
        <div
          className="relative group cursor-pointer transition-transform duration-200"
          style={{
            transform: `scale(${markerScale}) translateY(-${positionData.verticalOffset}px)`,
            transformOrigin: "bottom center",
            zIndex: zIndex,
            padding: "10px",
            margin: "-10px",
          }}
          onMouseEnter={() => handleMouseEnter(id)}
          onMouseLeave={handleMouseLeave}
        >
          <div className="flex flex-col hover:scale-110">
            <div
              className="flex flex-row items-stretch h-10 overflow-hidden rounded-t-lg"
              style={{
                border: `2px solid ${tripColor}`,
                borderBottom: `2px solid ${tripColor}`,
              }}
            >
              {/* Left side - Sequence number */}
              <div
                className="relative w-10 overflow-hidden"
                style={{ backgroundColor: tripColor }}
              >
                {!hasTemporarySequence ? (
                  <div className="relative h-full flex items-center justify-center">
                    <span className="text-white text-sm font-medium z-10">
                      {sequence}
                    </span>
                  </div>
                ) : (
                  <div className="relative h-full flex items-center justify-center bg-red-500 w-full">
                    <span className="text-white text-sm font-medium z-10">
                      {temporaryWaypointOrder?.[waypoint.id]}
                    </span>
                  </div>
                )}
              </div>
              {/* Right side - Pickup and Delivery counts */}
              {showCounts && (
                <div className="flex flex-col w-6">
                  <div className="flex-1 bg-blue-600 flex items-center justify-center">
                    <span className="text-white text-sm font-medium">
                      {pickupCount}
                    </span>
                  </div>
                  <div className="flex-1 bg-purple-400 flex items-center justify-center">
                    <span className="text-white text-sm font-medium">
                      {deliveryCount}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Progress indicator appended at the bottom as a separate element */}
            {!hasTemporarySequence && (
              <div
                className="w-full overflow-hidden rounded-b-lg"
                style={{
                  height: `${getProgressIndicatorHeight()}px`,
                  backgroundColor: "rgb(107, 114, 128)", // bg-gray-500
                  borderLeft: `2px solid ${tripColor}`,
                  borderRight: `2px solid ${tripColor}`,
                  borderBottom: `2px solid ${tripColor}`,
                  marginTop: "-2px",
                }}
              >
                <div
                  className="h-full bg-[#4CAF50]"
                  style={{ width: `${completionPercentage}%` }}
                />
              </div>
            )}
          </div>
          {/* Connector line and dot */}
          <div className="flex flex-col items-center">
            <div
              className="w-0.5 bg-black"
              style={{ height: `${positionData.connectorLength}px` }}
            ></div>
            <div
              className="w-3 h-3 border-[1px] border-white rounded-full overflow-hidden"
              style={{
                background: dotBackground,
                boxShadow: hasMultipleColors
                  ? "0 1px 2px rgba(0,0,0,0.3)"
                  : "none",
              }}
            ></div>
          </div>
        </div>
      </AdvancedMarker>
    );
  });
}
